//LoginPage.tsx

import React, { useState } from "react";
import { ACCENTURE_LOGO_SRC, T, injectTheme, injectLandingTheme } from "../theme/theme";
import { auth_login } from "../API";
import { useAuth } from "../hooks/useAuth";


const FEATURES = [
  {
    color: T.brand,
    bg: T.brandLight,
    title: 'AI-Powered Rule Generation',
    desc: 'Profile data and auto-generate DQ rules with confidence scoring',
  },
  {
    color: T.purple,
    bg: T.purpleBg,
    title: 'Governance & Compliance',
    desc: 'Audit-ready policy artifacts aligned to enterprise standards',
  },
  {
    color: T.amber,
    bg: T.amberBg,
    title: 'MDM Modernization Accelerators',
    desc: 'Migrate legacy configurations with AI-guided transformation',
  },
];


export default function LoginPage() {
  React.useEffect(() => {
    injectTheme();
    injectLandingTheme();
  }, []);

  const { setUser }           = useAuth();
  const [email, setEmail]     = useState("");
  const [password, setPass]   = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await auth_login(email, password);
      setUser({
        user_id:    data.user_id ?? "",
        email:      data.email,
        full_name:  data.full_name,
        is_admin:   data.is_admin,
        last_login: null,
        entitlements: data.entitlements,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="gwl-root" style={{ display: "flex", flexDirection: "column" }}>
      {/* Reuse the same header shell as Landing */}
      <header className="gw-app-header">
        <div className="gw-app-header-brand">
          <img src={ACCENTURE_LOGO_SRC} alt="Accenture" height={22} />
          <div className="gw-app-header-divider" />
          <div>
            <div className="gw-app-header-wordmark">Knowledge Suite</div>
            <div className="gw-app-header-sub">
              {'DMG Data Agents'}
            </div>
          </div>
        </div>
      </header>

      {/* Hero strip — mirrors landing layout */}
      <div style={S.hero}>
        <div style={S.heroLeft} className="gwl-hero-rise">
          <div>
            <div className="gwl-hero-eyebrow gwl-hero-rise">Intelligent Data Agents</div>
            <h1 className="gwl-hero-title gwl-hero-rise">
              Every step of the<br />
              <em>data-to-insight</em><br />
              journey for DMG
            </h1>
            <p className="gwl-hero-desc gwl-hero-rise">
              AI-powered governance, data quality, and master data management —
              from raw ingestion to audit-ready enterprise insight.
            </p>
          </div>

          {/* Feature dots — same as landing */}
          <div className="gwl-hero-right gwl-hero-rise mt-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="gwl-feature-item">
                <div
                  className="gwl-feature-dot"
                  style={{ background: f.color, boxShadow: `0 0 0 3px ${f.bg}` }}
                />
                <div>
                  <div className="gwl-feature-title">{f.title}</div>
                  <div className="gwl-feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Login card — matches gwl-hero-right style */}
        <div className="gwl-hero-right gwl-hero-rise" style={S.card}>
          <div style={S.cardHeader}>
            <div className="gwl-tile-idx">Sign in</div>
          </div>

          {error && (
            <div style={S.errorBox}>
              <span style={{ marginRight: 8 }}>⚠</span>{error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="gw-form-group" style={{ marginBottom: 14 }}>
              <label className="gw-label">Email address</label>
              <input
                className="gw-input"
                type="email"
                value={email}
                autoComplete="email"
                placeholder="you@company.com"
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="gw-form-group" style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label className="gw-label">Password</label>
                
                <a  href="/forgot-password"
                  style={{ fontSize: 11, color: T.brand, textDecoration: "none" }}
                >
                  Forgot password?
                </a>
              </div>
              <input
                className="gw-input"
                type="password"
                value={password}
                autoComplete="current-password"
                placeholder="••••••••"
                onChange={(e) => setPass(e.target.value)}
                required
              />
            </div>

            <button
              className="gw-btn gw-btn-primary"
              style={{ width: "100%", justifyContent: "center", padding: "11px 0", fontSize: 13 }}
              disabled={loading}
            >
              {loading ? "Signing in…" : "Sign in →"}
            </button>
          </form>

          <div style={S.divider}>
            <div style={S.dividerLine} />
            <span style={S.dividerLabel}>secure · encrypted · session-bound</span>
            <div style={S.dividerLine} />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            {["DQ Rules", "MDM", "Governance"].map((tag) => (
              <span key={tag} className="gw-badge gw-badge-brand">{tag}</span>
            ))}
          </div>
        </div>
      </div>

      <footer className="gwl-footer">
        <div className="gwl-footer-txt">© 2025 Accenture · All rights reserved</div>
        <div className="gwl-footer-txt">Build 2025.06</div>
      </footer>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  hero: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "64px 48px 56px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 48,
    alignItems: "center",
    flex: 1,
  },
  heroLeft: { display: "flex", flexDirection: "column" },
  card: {
    background: "#fff",
    border: `1px solid #c8d4e0`,
    borderRadius: 16,
    padding: 32,
    boxShadow: "0 4px 24px rgba(30,58,110,.07)",
  },
  cardHeader: {
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: `1px solid #c8d4e0`,
  },
  errorBox: {
    background: "#fef2f2",
    border: "1px solid #fca5a5",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#991b1b",
    fontSize: 13,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "20px 0 16px",
  },
  dividerLine: { flex: 1, height: 1, background: "#c8d4e0" },
  dividerLabel: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: ".12em",
    textTransform: "uppercase" as const,
    color: "#445566",
    whiteSpace: "nowrap" as const,
  },
};